// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";

import { Opportunity } from "../../WorkflowsStore";
import { arsErsSectionEditedByCurrentUser } from "../../WorkflowsStore/Opportunity/UsTx/UsTxArsErsSharedUtils";
import { FormContainer } from "../Paperwork/FormContainer";
import FormViewer from "../Paperwork/FormViewer";
import { PrintablePageContainer } from "../Paperwork/styles";
import ERSChecklist from "../Paperwork/US_TX/UsTxEarlyReleaseFromSupervision/ERS-Checklist";
import { useOpportunitySidePanel } from "../WorkflowsJusticeInvolvedPersonProfile/OpportunitySidePanelContext";

const WorkflowsUsTxEarlyReleaseFromSupervisionForm = observer(
  function WorkflowsUsTxEarlyReleaseFromSupervisionForm({
    opportunity,
  }: {
    opportunity: Opportunity;
  }) {
    const { setCurrentView } = useOpportunitySidePanel();
    const formRef = React.useRef<HTMLDivElement>(null);
    const [isMissingContent, setIsMissingContent] = useState(false);

    useEffect(() => {
      // prevent the ability to download form before it has been rendered
      const pages = formRef.current?.querySelectorAll(PrintablePageContainer);
      setIsMissingContent(!(pages && pages.length > 0));
    }, [formRef]);

    const sectionEditedByCurrentUser = arsErsSectionEditedByCurrentUser({
      currentUserId: opportunity.form?.currentUserId,
      draftData: opportunity.form?.draftData,
      fieldAuthors: opportunity.form?.fieldAuthors,
    });

    return (
      <FormContainer
        agencyName="TDCJ"
        heading={opportunity.config.label}
        isMissingContent={isMissingContent}
        onClickDownload={async () => opportunity.form?.fillAndSaveFile()}
        downloadButtonLabel="Download Form"
        opportunity={opportunity}
        onDenialButtonClick={() => {
          setCurrentView("MARK_INELIGIBLE");
        }}
      >
        <FormViewer formRef={formRef}>
          <ERSChecklist
            sectionEditedByCurrentUser={sectionEditedByCurrentUser}
          />
        </FormViewer>
      </FormContainer>
    );
  },
);

export default WorkflowsUsTxEarlyReleaseFromSupervisionForm;
